import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';

import { getDqtWalletService, getDqtLedgerService } from '../../../services/billing.service';

/**
 * DqtWalletCard — the DQ Token (DQT) balance, the plan's DQ Token prices, and the
 * latest DQ Token movements.
 *
 * A partner holds DQ Tokens only (product-owner decision, 17 Sep 2026). They are
 * spent where they are used, at the prices on its plan: converted live when the
 * partner assigns licences and credits to a network or school, and charged per
 * credit when the partner builds a program or assessment itself. There is no
 * separate conversion into credits or licences held by the workspace.
 *
 * Every rule lives on the server. The card only renders what /dqt/wallet says:
 *   - dqt_visible false  → a direct workspace, which never sees DQT: render nothing
 *
 * `onTopUp` is passed only when the workspace actually has packs on sale, so the
 * Top up button never points at a section that is not there. The page owns that
 * knowledge — the purchase panel tells it.
 */

const TEAL = '#0097b2';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

const describeEntry = (e) => {
    if (e.transaction_type === 'grant') return `Granted · ref ${e.payment_reference || '—'}`;
    if (e.transaction_type === 'purchase') return 'Purchased';
    if (e.transaction_type === 'assignment') {
        const a = e.assignment || {};
        const parts = [a.licences ? `${fmt(a.licences)} licences` : null, a.credits ? `${fmt(a.credits)} credits` : null].filter(Boolean).join(' and ');
        return `Assigned ${parts || 'to'}${a.org_name ? ` to ${a.org_name}` : ''}`;
    }
    if (e.transaction_type === 'build') return e.description || 'Build';
    if (e.transaction_type === 'conversion') return `Converted into ${fmt(e.conversion?.quantity)} ${e.conversion?.unit === 'licence' ? 'licences' : 'credits'}`;
    return e.description || 'Adjustment';
};

const DqtWalletCard = ({ onTopUp, planName = '' }) => {
    const [wallet, setWallet] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const [w, l] = await Promise.all([
                getDqtWalletService(),
                getDqtLedgerService({ limit: 5 }),
            ]);
            setWallet(w?.data || null);
            setEntries(l?.data?.entries || []);
        } catch (_) {
            // invokeApi has already shown the error; the card simply stays hidden.
            setWallet(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading || !wallet || !wallet.dqt_visible) return null;

    const creditPrice = wallet.credit_price_dqt;
    const licencePrice = wallet.licence_price_dqt;

    return (
        <Box sx={{
            flex: '1 1 320px',
            background: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #E5E7EB)',
            borderRadius: '20px', p: '28px 28px 24px',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            position: 'relative', overflow: 'hidden',
        }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: TEAL, borderRadius: '20px 20px 0 0' }}/>

            <Box>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', letterSpacing: '1.5px', textTransform: 'uppercase', mb: 1.25 }}>
                    DQ Token Balance
                </Typography>
                {planName && (
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 1.25 }}>
                        {planName}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 56, fontWeight: 900, color: TEAL, lineHeight: 1 }}>
                        {fmt(wallet.dqt_balance)}
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: TEAL, textTransform: 'uppercase', mb: 0.6 }}>
                        DQT
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.825rem', color: '#94a3b8' }}>
                    {typeof creditPrice === 'number'
                        ? <>1 credit = <strong style={{ color: 'var(--brand-text-muted, #475569)' }}>{fmt(creditPrice)} DQT</strong></>
                        : 'No DQ Token price for credits yet'}
                    {' · '}
                    {typeof licencePrice === 'number'
                        ? <>1 licence = <strong style={{ color: 'var(--brand-text-muted, #475569)' }}>{fmt(licencePrice)} DQT</strong></>
                        : 'no price for licences yet'}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mt: 0.75 }}>
                    DQ Tokens are converted when you assign licenses and credits to a school, and when you build.
                </Typography>

                {entries.length > 0 && (
                    <Box sx={{ mt: 2.5 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', letterSpacing: '1px', textTransform: 'uppercase', mb: 0.75 }}>
                            Recent
                        </Typography>
                        {entries.map((e) => (
                            <Box key={e._id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderTop: '1px solid var(--brand-border, #F1F5F9)' }}>
                                <Typography sx={{ fontSize: '0.8rem', color: 'var(--brand-text-muted, #475569)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {describeEntry(e)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: e.amount_dqt >= 0 ? '#16a34a' : '#475569', whiteSpace: 'nowrap' }}>
                                    {e.amount_dqt >= 0 ? '+' : '−'}{fmt(Math.abs(e.amount_dqt))} DQT
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {onTopUp && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={onTopUp}
                        sx={{ backgroundColor: TEAL, color: '#fff', borderRadius: '10px', textTransform: 'none', fontWeight: 700,
                            px: 3, py: 1, fontSize: '0.875rem', boxShadow: 'none', whiteSpace: 'nowrap',
                            '&:hover': { backgroundColor: '#007f96', boxShadow: '0 4px 12px rgba(0,151,178,0.25)' } }}
                    >
                        Top up
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default DqtWalletCard;
